import classNames from "classnames";
import Color from "color";
import { MisalignmentItem } from "PFApp/booking/types";
import { RowType } from "PFApp/booking/types/row";
import { useThresholds } from "PFCore/hooks/queries/bookings/thresholds/use_thresholds";
import MisalignmentWarningIconSvg from "PFIcons/calendar_misalignment.svg";
import WarningIconSvg from "PFIcons/warning.svg";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import MisalignmentErrorDetails from "./misalignment_error_details";
import css from "./warning_icons.module.scss";
import WarningTooltipIcon from "./warning_tooltip_icon";

const WarningIcon = (props) => <WarningIconSvg viewBox="0 0 20 18" {...props} />;
const MisalignmentWarningIcon = (props) => <MisalignmentWarningIconSvg {...props} />;

interface IProps {
  overbookings?: { date: string; utilization: number }[];
  misalignments?: MisalignmentItem[];
  type?: string;
  iconNotHighlighted?: boolean;
  scrollToDate?: () => void;
  roleName?: string;
}

const WarningIcons = ({
  overbookings,
  misalignments,
  type,
  iconNotHighlighted,
  scrollToDate,
  roleName
}: IProps) => {
  const thresholds = useThresholds(["overbooking"]);
  const { t } = useTranslation("bookingModule");
  const overbookedCounter = overbookings?.length || 0;
  const misalignmentsCounter = misalignments?.length || 0;

  const overbookingMaxLoad = useMemo(
    () => overbookings && Math.max(...(overbookings?.map((item) => item.utilization) || [])),
    [overbookings]
  );

  const overbookingBackgroundColor = useMemo(() => {
    const backgroundColor: string | undefined =
      (!!overbookingMaxLoad &&
        thresholds?.overbooking?.find(
          (item) => overbookingMaxLoad * 100 >= item.min && overbookingMaxLoad * 100 <= item.max
        )?.color) ||
      undefined;

    return backgroundColor ? new Color(backgroundColor) : undefined;
  }, [overbookingMaxLoad]);

  return (
    <div className={css.iconsWrapper}>
      {!!overbookedCounter && (
        <WarningTooltipIcon
          IconComponent={WarningIcon}
          label={t("workforce.overbookedWarning")}
          iconColor={overbookingBackgroundColor?.hex()}
          counter={overbookedCounter}
          classes={{
            icon: css.overbooked,
            counter: classNames(css.overbookedCounter, {
              [css.lightText]: !!overbookingBackgroundColor?.isDark()
            })
          }}
          height={18}
          width={18}
        />
      )}
      {!!misalignmentsCounter && (
        <WarningTooltipIcon
          IconComponent={MisalignmentWarningIcon}
          label={t("projects.misalignment")}
          counter={misalignmentsCounter}
          classes={{
            icon: classNames(css.misalignment, {
              [css.misalignmentNotHighlighted]: !!iconNotHighlighted
            }),
            counter: css.misalignmentCounter
          }}
          renderExpanded={
            type === RowType.Profile && !!misalignments
              ? () => (
                  <MisalignmentErrorDetails
                    roleName={roleName}
                    misalignments={misalignments}
                    onLinkClick={scrollToDate}
                  />
                )
              : undefined
          }
          height={15}
          width={13}
        />
      )}
    </div>
  );
};

export default WarningIcons;
